import { ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useQueryActiveApi } from '../../hooks/api/analysis/useQueryActiveAPI'
import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import PageLayout from '../../components/layout/PageLayout'
import WizSelect from '../../components/select/WizSelect'

interface IProps {
    className?: string
}

// 색상 팔레트를 조금 더 다양하고 구분이 잘 되는 색상으로 확장했습니다.
const COLORS = [
    '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
    '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe',
    '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000',
    '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080'
];

const ActiveKeysDashBoard: React.FC<IProps> = () => {
    const [limit, setLimit] = useState(100)
    const [hideExchange, setHide] = useState<Record<string, boolean>>({})
    const { data, refetch } = useQueryActiveApi(limit)

    // 1. 차트 데이터 가공
    const charts = useMemo(() => {
        if (!data) return []

        return data.map(el => {
            return {
                ...el.exchanges,
                date: moment.utc(el.date, 'YYYYMMDD').local().format('YYYY/MM/DD'),
                total: el.total,
                originalDate: el.date
            }
        })
    }, [data])

    const allExchangeKeys = useMemo(() => {
        if (!data) return []
        const keys = new Set<string>()

        data.forEach(el => {
            if (el.exchanges) {
                Object.keys(el.exchanges).forEach(k => keys.add(k))
            }
        })
        return Array.from(keys)
    }, [data])

    useEffect(() => {
        refetch()
    }, [limit])

    return (
        <PageLayout title="Lot active API Key Count">
            <div className={'flex space-x-[10px] mb-4'}>
                <WizSelect optionKey={'graph_limit'} value={limit} onChange={e => setLimit(e as number)} />
            </div>

            <div className="w-full h-[700px] bg-white rounded-lg shadow-sm p-4">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={charts} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <XAxis
                            dataKey="date"
                            minTickGap={30} // 날짜 겹침 방지
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis
                            tickFormatter={(value) => value.toLocaleString()} // 1,000 단위 콤마
                            width={60}
                        />
                        <Tooltip
                            formatter={(value: number) => value.toLocaleString()} // 툴팁 숫자 콤마
                            labelStyle={{ color: '#333' }}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        />
                        <Legend
                            wrapperStyle={{ paddingTop: '20px' }}
                            onClick={(e: any) => {
                                setHide(prev => ({
                                    ...prev,
                                    [e.dataKey]: !prev[e.dataKey],
                                }))
                            }}
                        />

                        {allExchangeKeys.map((key, index) => (
                            <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                stroke={COLORS[index % COLORS.length]}
                                strokeWidth={2} // 선 두께 약간 증가
                                dot={false} // 지저분한 점 제거
                                activeDot={{ r: 6 }}
                                connectNulls={true} // 데이터가 중간에 비어도 선 이어주기
                                hide={hideExchange[key] === true}
                                name={key} // 범례에 표시될 이름
                            />
                        ))}

                        <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#000000" // Total은 검정색 혹은 눈에 띄는 색
                            strokeWidth={3} // 더 굵게
                            dot={false}
                            activeDot={{ r: 6 }}
                            connectNulls={true}
                            hide={hideExchange['total'] === true}
                            name="Total"
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </PageLayout>
    )
}

export default ActiveKeysDashBoard